<template>
  <section class="section -flat-tablet -alt row -tablet">
    <div class="section__content row__item">
      <h2 class="section__legend">{{ legend }}</h2>
      <h3 class="section__title">{{ title }}</h3>
      <p>{{ text }}</p>
    </div>
    <Transition name="fade" mode="out-in">
      <form v-if="isFormVisible" class="row__item -span-10 -skip-1" @submit.prevent="onSubmit">
        <InputText
          v-model="model.name"
          name="name"
          :label="$page.contactFormNameLabel"
          :input-class="['-contact']"
          :error="error.isNameInvalid"
          :error-message="errorMessage.name"
          @validate="validateName"
        />

        <InputText
          v-model="model.email"
          name="name"
          :label="$page.contactFormEmailLabel"
          :input-class="['-contact']"
          :error="error.isEmailInvalid"
          :error-message="errorMessage.email"
          @validate="validateEmail"
        />
        <InputText
          v-model="model.message"
          name="message"
          :label="$page.contactFormMessageLabel"
          :input-class="['-contact']"
          textarea
          :error="error.isMessageInvalid"
          :error-message="errorMessage.message"
          @validate="validateMessage"
        />

        <div>
          <p v-if="isRecaptchaErrorVisible" class="input__error">
            {{ $page.contactFormRecaptchaError }}
          </p>
        </div>

        <button class="button -contact-submit" type="submit">
          {{ $page.contactFormSubmitLabel }}
        </button>
      </form>

      <div v-else-if="isConfirmCardVisible" class="row__item -span-7 -skip-1 confirm-card">
        <div class="confirm-card__content">
          <Icon id="check" />
          <h3 class="confirm-card__title">{{ $page.contactFormConfirmCardHeader }}</h3>
          <p>{{ $page.contactFormConfirmCardText }}</p>
          <button class="button confirm-card__button" @click="hideConfirmCard">
            <span>{{ $page.contactFormConfirmCardButton }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script>
import { useReCaptcha } from 'vue-recaptcha-v3'

import Icon from '@/components/global/Icon.vue'
import InputText from '@/components/inputs/InputText.vue'

export default {
  name: 'ContactForm',
  components: { Icon, InputText },
  setup() {
    const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()
    return { executeRecaptcha, recaptchaLoaded }
  },
  props: {
    legend: { type: String, required: true },
    text: { type: String, required: true },
    title: { type: String, required: true },
  },
  data: () => ({
    isFormVisible: true,
    isConfirmCardVisible: false,
    isRecaptchaValid: false,
    isRecaptchaErrorVisible: false,
    model: {
      name: '',
      email: '',
      message: '',
    },
    error: {
      isNameInvalid: false,
      isEmailInvalid: false,
      isMessageInvalid: false,
    },
    errorMessage: {
      name: '',
      email: '',
      message: '',
    },
    regex: {
      name: /^[a-zA-ZÀ-ÿ\- ]{1,50} [a-zA-ZÀ-ÿ\- ]{1,50}$/,
      email:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: /^[0-9a-zA-ZÀ-ÿ\- ]{1,500}$/,
    },
  }),
  methods: {
    onSubmit() {
      this.validateName()
      this.validateEmail()
      this.validateMessage()

      if (!this.isRecaptchaValid) {
        this.isRecaptchaErrorVisible = true
      }

      this.executeRecaptcha('contact')
        .then((token) => {
          this.isRecaptchaValid = true
          this.isRecaptchaErrorVisible = false

          const isFormValid = this.validateForm()
          if (isFormValid) {
            const { name, email, message } = this.model
            this.showConfirmCard()
            this.resetForm()

            fetch(`${import.meta.env.VITE_APP_API_URL}/contact`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ name, email, message, recaptchaToken: token }),
            })
          }
        })
        .catch(() => {
          this.isRecaptchaValid = false
          this.isRecaptchaErrorVisible = true
        })
    },
    validateForm() {
      let isFormValid = true
      if (this.error.isNameInvalid) {
        isFormValid = false
      }
      if (this.error.isEmailInvalid) {
        isFormValid = false
      }
      if (this.error.isMessageInvalid) {
        isFormValid = false
      }
      return isFormValid
    },
    resetForm() {
      this.model.name = ''
      this.model.email = ''
      this.model.message = ''
    },
    validateName() {
      if (!this.model.name) {
        this.error.isNameInvalid = true
        this.errorMessage.name = this.$page.contactFormNameErrorEmpty
      } else if (!this.regex.name.test(this.model.name)) {
        this.error.isNameInvalid = true
        this.errorMessage.name = this.$page.contactFormNameErrorPattern
      } else {
        this.error.isNameInvalid = false
      }
    },
    validateEmail() {
      if (!this.model.email) {
        this.error.isEmailInvalid = true
        this.errorMessage.email = this.$page.contactFormEmailErrorEmpty
      } else if (!this.regex.email.test(this.model.email.toLowerCase())) {
        this.error.isEmailInvalid = true
        this.errorMessage.email = this.$page.contactFormEmailErrorPattern
      } else {
        this.error.isEmailInvalid = false
      }
    },
    validateMessage() {
      if (!this.model.message) {
        this.error.isMessageInvalid = true
        this.errorMessage.message = this.$page.contactFormMessageErrorEmpty
      } else if (!this.regex.message.test(this.model.message)) {
        this.error.isEmailInvalid = true
        this.errorMessage.message = this.$page.contactFormMessageErrorPattern
      } else {
        this.error.isMessageInvalid = false
      }
    },
    showConfirmCard() {
      this.isFormVisible = false
      if (this.isConfirmCardVisible === false) {
        this.isConfirmCardVisible = true
      }
    },
    hideConfirmCard() {
      if (this.isConfirmCardVisible === true) {
        this.isConfirmCardVisible = false
      }
      this.isFormVisible = true
    },
  },
}
</script>
